import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { orpc } from '@/lib/orpc-client'
import { UiBlocksFlagContext } from '@/lib/ui-blocks-flag'
import { Button } from '@/components/ui/button'

type ProjectDetails = Awaited<ReturnType<typeof orpc.projects.get>>

interface ProjectContextValue {
  projectId: string
  project: ProjectDetails
  isLoading: false
  reload: () => void
}

interface ProjectContextLoading {
  projectId: string
  project: null
  isLoading: true
}

type ProjectContextState = ProjectContextValue | ProjectContextLoading

const ProjectContext = createContext<ProjectContextState | undefined>(undefined)

export function ProjectProvider({
  projectId,
  children,
  fallback,
  errorFallback,
}: {
  projectId: string
  children: ReactNode
  fallback?: ReactNode
  errorFallback?: (error: Error) => ReactNode
}) {
  // Every result is tagged with the id it belongs to: after a switch the
  // previous project is never handed to the next one's tree, not even for the
  // one render before the fetch effect runs (board uvu5).
  const [loaded, setLoaded] = useState<{ projectId: string; project: ProjectDetails } | null>(null)
  const [failed, setFailed] = useState<{ projectId: string; error: Error } | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const project = loaded?.projectId === projectId ? loaded.project : null
  const error = failed?.projectId === projectId ? failed.error : null
  const isLoading = !project && !error

  useEffect(() => {
    if (project && reloadKey === 0) return
    // A reload (Settings save, a `project:changed` push) with this project
    // already on screen: a failure keeps the loaded copy instead of swapping
    // the whole layout for the error page.
    const hasLoaded = project !== null

    let cancelled = false
    orpc.projects
      .get({ projectId })
      .then((result) => {
        if (cancelled) return
        setLoaded({ projectId, project: result })
        setFailed(null)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        if (hasLoaded) {
          console.warn('[project] reload failed, keeping the loaded project', err)
          return
        }
        setFailed({
          projectId,
          error: err instanceof Error ? err : new Error('Failed to load project'),
        })
      })

    return () => {
      cancelled = true
    }
    // `project` is derived from the id; a reload is the only other trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, reloadKey])

  const reload = () => setReloadKey((k) => k + 1)

  const value = useMemo<ProjectContextState>(() => {
    if (!project) {
      return { projectId, project: null, isLoading: true as const }
    }
    return { projectId, project, isLoading: false as const, reload }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, project])

  if (error) {
    if (errorFallback) return <>{errorFallback(error)}</>
    return <ProjectLoadError />
  }

  if (isLoading) {
    return <>{fallback ?? null}</>
  }

  return (
    <ProjectContext.Provider value={value}>
      <UiBlocksFlagContext.Provider value={value.project?.features?.uiBlocks !== false}>
        {children}
      </UiBlocksFlagContext.Provider>
    </ProjectContext.Provider>
  )
}

/** Default "project not found / no access" screen with a link back to the app root. */
export function ProjectLoadError() {
  const { t } = useTranslation()
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <h2 className="text-lg font-semibold">{t('project.notFoundTitle')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('project.notFoundBody')}</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/">{t('project.goHome')}</Link>
        </Button>
      </div>
    </div>
  )
}

export function useProject(): ProjectContextValue {
  const context = useContext(ProjectContext)
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider')
  }
  if (context.isLoading) {
    throw new Error('useProject called while project is still loading')
  }
  return context
}

export function useOptionalProject(): ProjectContextValue | null {
  const context = useContext(ProjectContext)
  if (!context || context.isLoading) {
    return null
  }
  return context
}
